/**
 * WidgetErrorBoundary — локальный error boundary для виджетов/страниц.
 *
 * Любой throw в рендере поддерева заменяется на «Данные недоступны»
 * (или переданный `fallback`) вместо белого экрана всего приложения.
 * Сброс состояния — через `key` снаружи (например, key={kato}), чтобы
 * при смене контекста виджет пробовал отрисоваться заново.
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

interface BoundaryProps {
  fallback: ReactNode;
  children: ReactNode;
}

interface BoundaryState {
  hasError: boolean;
}

class Boundary extends Component<BoundaryProps, BoundaryState> {
  state: BoundaryState = { hasError: false };

  static getDerivedStateFromError(): BoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('WidgetErrorBoundary caught', error, info.componentStack);
  }

  render() {
    return this.state.hasError ? this.props.fallback : this.props.children;
  }
}

interface WidgetErrorBoundaryProps {
  children: ReactNode;
  /** Замена дефолтного текста «Данные недоступны». */
  fallback?: ReactNode;
}

export const WidgetErrorBoundary = ({
  children,
  fallback,
}: WidgetErrorBoundaryProps) => {
  const { t } = useTranslation();
  return (
    <Boundary
      fallback={
        fallback ?? (
          <p className="p-4 text-sm text-text-muted" data-testid="widget-error">
            {t('common.dataUnavailable')}
          </p>
        )
      }
    >
      {children}
    </Boundary>
  );
};
